import { useEffect, useState } from "react";
import { Save, ChevronDown, Plus, Trash2, Image as ImageIcon } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { HOME_DEFAULTS } from "@/data";
import MediaLibrary from "@/admin/MediaLibrary";

const clone = (o) => JSON.parse(JSON.stringify(o));
const inp = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand-magenta";
const linesToArr = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const arrToLines = (a) => (a || []).join("\n");

const Section = ({ title, children, open, onToggle }) => (
  <div className="mb-4 overflow-hidden rounded-xl border border-slate-200 bg-white">
    <button onClick={onToggle} className="flex w-full items-center justify-between px-5 py-4 text-left font-heading text-lg font-semibold">
      {title}
      <ChevronDown className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
    {open && <div className="border-t border-slate-100 p-5">{children}</div>}
  </div>
);

const F = ({ label, children }) => (
  <div className="mb-4"><label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</label>{children}</div>
);

export default function HomepageEditor() {
  const [h, setH] = useState(null);
  const [open, setOpen] = useState("hero");
  const [status, setStatus] = useState("");
  const [pickCb, setPickCb] = useState(null);

  useEffect(() => {
    api.get("/homepage").then((r) => {
      const merged = clone(HOME_DEFAULTS);
      Object.keys(r.data || {}).forEach((k) => { if (r.data[k]) merged[k] = r.data[k]; });
      setH(merged);
    }).catch(() => setH(clone(HOME_DEFAULTS)));
  }, []);

  const upd = (mut) => setH((prev) => { const c = clone(prev); mut(c); return c; });
  const save = async () => {
    setStatus("saving");
    try { await api.put("/homepage", { value: h }); setStatus("saved"); setTimeout(() => setStatus(""), 1800); }
    catch { setStatus("error"); }
  };
  const openPicker = (cb) => setPickCb(() => cb);

  if (!h) return <div className="text-slate-400">Loading…</div>;

  const ImgBtn = ({ value, onChange }) => (
    <div className="flex items-center gap-2">
      <div className="h-12 w-16 shrink-0 overflow-hidden rounded border border-slate-200 bg-slate-100">
        {value ? <img src={mediaUrl(value)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-slate-300"><ImageIcon className="h-4 w-4" /></div>}
      </div>
      <input className={inp} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL" />
      <button type="button" onClick={() => openPicker(onChange)} className="shrink-0 rounded-full border border-slate-200 px-3 py-2 text-xs font-medium hover:border-brand-magenta hover:text-brand-magenta">Pick</button>
    </div>
  );

  const Rows = ({ arr, path, tpl, render }) => (
    <div className="space-y-3">
      {arr.map((row, i) => (
        <div key={i} className="rounded-lg border border-slate-200 p-3">
          <div className="mb-2 flex justify-end">
            <button onClick={() => upd((c) => { const a = path(c); a.splice(i, 1); })} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
          </div>
          {render(row, i)}
        </div>
      ))}
      <button onClick={() => upd((c) => path(c).push(clone(tpl)))} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium hover:border-brand-magenta hover:text-brand-magenta"><Plus className="h-3.5 w-3.5" /> Add</button>
    </div>
  );

  const hero = h.hero, brand = h.brand, impact = h.impact, why = h.why, sol = h.solutions, feat = h.featured, ft = h.footer;

  return (
    <div data-testid="homepage-editor" className="max-w-3xl pb-24">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Homepage &amp; Footer</h1>
          <p className="text-sm text-slate-500">Edit every section of the homepage and the footer.</p>
        </div>
        <div className="flex items-center gap-3">
          {status === "saved" && <span className="text-sm text-green-600">Saved ✓</span>}
          {status === "error" && <span className="text-sm text-red-500">Error</span>}
          <button onClick={save} data-testid="save-homepage-btn" className="inline-flex items-center gap-2 rounded-full bg-brand-magenta px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90">
            <Save className="h-4 w-4" /> {status === "saving" ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <Section title="Hero + Slider" open={open === "hero"} onToggle={() => setOpen(open === "hero" ? "" : "hero")}>
        <F label="Badge"><input className={inp} value={hero.badge} onChange={(e) => upd((c) => (c.hero.badge = e.target.value))} /></F>
        <F label="Headline lines (one per line, add ' | grad' to gradient-style a line)">
          <textarea rows={4} className={inp} value={(hero.headline || []).map((l) => l.text + (l.grad ? " | grad" : "")).join("\n")}
            onChange={(e) => upd((c) => (c.hero.headline = linesToArr(e.target.value).map((l) => { const g = /\|\s*grad$/i.test(l); return { text: l.replace(/\|\s*grad$/i, "").trim(), grad: g }; })))} />
        </F>
        <F label="Subheadline"><textarea rows={2} className={inp} value={hero.subheadline} onChange={(e) => upd((c) => (c.hero.subheadline = e.target.value))} /></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Primary CTA label"><input className={inp} value={hero.ctaPrimary?.label} onChange={(e) => upd((c) => (c.hero.ctaPrimary.label = e.target.value))} /></F>
          <F label="Primary CTA link"><input className={inp} value={hero.ctaPrimary?.href} onChange={(e) => upd((c) => (c.hero.ctaPrimary.href = e.target.value))} /></F>
          <F label="Secondary CTA label"><input className={inp} value={hero.ctaSecondary?.label} onChange={(e) => upd((c) => (c.hero.ctaSecondary.label = e.target.value))} /></F>
          <F label="Secondary CTA link"><input className={inp} value={hero.ctaSecondary?.href} onChange={(e) => upd((c) => (c.hero.ctaSecondary.href = e.target.value))} /></F>
        </div>
        <F label="Credibility strip (one per line: Value | Label)">
          <textarea rows={3} className={inp} value={(hero.credibility || []).map((x) => `${x.value} | ${x.label}`).join("\n")}
            onChange={(e) => upd((c) => (c.hero.credibility = linesToArr(e.target.value).map((l) => { const [value, ...r] = l.split("|"); return { value: (value || "").trim(), label: r.join("|").trim() }; })))} />
        </F>
        <F label="Slider slides">
          <Rows arr={hero.slides || []} path={(c) => c.hero.slides} tpl={{ src: "", label: "", place: "" }} render={(row, i) => (
            <div className="space-y-2">
              <ImgBtn value={row.src} onChange={(v) => upd((c) => (c.hero.slides[i].src = v))} />
              <div className="grid grid-cols-2 gap-2">
                <input className={inp} placeholder="Label" value={row.label} onChange={(e) => upd((c) => (c.hero.slides[i].label = e.target.value))} />
                <input className={inp} placeholder="Place / sub-label" value={row.place} onChange={(e) => upd((c) => (c.hero.slides[i].place = e.target.value))} />
              </div>
            </div>
          )} />
        </F>
      </Section>

      <Section title="Brand Intro" open={open === "brand"} onToggle={() => setOpen(open === "brand" ? "" : "brand")}>
        <F label="Eyebrow"><input className={inp} value={brand.eyebrow} onChange={(e) => upd((c) => (c.brand.eyebrow = e.target.value))} /></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Heading (lead)"><input className={inp} value={brand.headingLead} onChange={(e) => upd((c) => (c.brand.headingLead = e.target.value))} /></F>
          <F label="Heading (accent, gradient color)"><input className={inp} value={brand.headingAccent} onChange={(e) => upd((c) => (c.brand.headingAccent = e.target.value))} /></F>
        </div>
        <F label="Body"><textarea rows={5} className={inp} value={brand.body} onChange={(e) => upd((c) => (c.brand.body = e.target.value))} /></F>
        <F label="CTA label"><input className={inp} value={brand.cta} onChange={(e) => upd((c) => (c.brand.cta = e.target.value))} /></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Floating stat value"><input className={inp} value={brand.statValue} onChange={(e) => upd((c) => (c.brand.statValue = e.target.value))} /></F>
          <F label="Floating stat label"><input className={inp} value={brand.statLabel} onChange={(e) => upd((c) => (c.brand.statLabel = e.target.value))} /></F>
        </div>
        <F label="Main image"><ImgBtn value={brand.image} onChange={(v) => upd((c) => (c.brand.image = v))} /></F>
        <F label="Secondary image"><ImgBtn value={brand.image2} onChange={(v) => upd((c) => (c.brand.image2 = v))} /></F>
      </Section>

      <Section title="Impact Stats" open={open === "impact"} onToggle={() => setOpen(open === "impact" ? "" : "impact")}>
        <div className="grid grid-cols-2 gap-3">
          <F label="Eyebrow"><input className={inp} value={impact.eyebrow} onChange={(e) => upd((c) => (c.impact.eyebrow = e.target.value))} /></F>
          <F label="Heading accent"><input className={inp} value={impact.headingAccent} onChange={(e) => upd((c) => (c.impact.headingAccent = e.target.value))} /></F>
        </div>
        <F label="Heading lead"><input className={inp} value={impact.headingLead} onChange={(e) => upd((c) => (c.impact.headingLead = e.target.value))} /></F>
        <F label="Stats (one per line: Value | Label | countTo — countTo optional number to animate)">
          <textarea rows={4} className={inp} value={(impact.stats || []).map((s) => `${s.value} | ${s.label}${s.animate ? " | " + s.to : ""}`).join("\n")}
            onChange={(e) => upd((c) => (c.impact.stats = linesToArr(e.target.value).map((l) => { const p = l.split("|").map((x) => x.trim()); const to = p[2] ? parseInt(p[2], 10) : null; return { value: p[0], label: p[1] || "", animate: !!to, to: to || 0 }; })))} />
        </F>
      </Section>

      <Section title="Why Organizations" open={open === "why"} onToggle={() => setOpen(open === "why" ? "" : "why")}>
        <div className="grid grid-cols-2 gap-3">
          <F label="Heading lead"><input className={inp} value={why.headingLead} onChange={(e) => upd((c) => (c.why.headingLead = e.target.value))} /></F>
          <F label="Heading accent"><input className={inp} value={why.headingAccent} onChange={(e) => upd((c) => (c.why.headingAccent = e.target.value))} /></F>
        </div>
        <F label="Cards (one per line: Number | Title | Description)">
          <textarea rows={6} className={inp} value={(why.cards || []).map((x) => `${x.n} | ${x.title} | ${x.desc}`).join("\n")}
            onChange={(e) => upd((c) => (c.why.cards = linesToArr(e.target.value).map((l) => { const p = l.split("|").map((x) => x.trim()); return { n: p[0], title: p[1] || "", desc: p[2] || "" }; })))} />
        </F>
        <F label="Closing tagline"><input className={inp} value={why.tagline} onChange={(e) => upd((c) => (c.why.tagline = e.target.value))} /></F>
      </Section>

      <Section title="Marquee" open={open === "marquee"} onToggle={() => setOpen(open === "marquee" ? "" : "marquee")}>
        <F label="Scrolling words (one per line)"><textarea rows={4} className={inp} value={arrToLines(h.marquee)} onChange={(e) => upd((c) => (c.marquee = linesToArr(e.target.value)))} /></F>
      </Section>

      <Section title="Experiences Designed (Solutions)" open={open === "sol"} onToggle={() => setOpen(open === "sol" ? "" : "sol")}>
        <div className="grid grid-cols-2 gap-3">
          <F label="Heading lead"><input className={inp} value={sol.headingLead} onChange={(e) => upd((c) => (c.solutions.headingLead = e.target.value))} /></F>
          <F label="Heading accent"><input className={inp} value={sol.headingAccent} onChange={(e) => upd((c) => (c.solutions.headingAccent = e.target.value))} /></F>
        </div>
        <F label="Intro"><textarea rows={2} className={inp} value={sol.intro} onChange={(e) => upd((c) => (c.solutions.intro = e.target.value))} /></F>
        <F label="Categories">
          <Rows arr={sol.categories || []} path={(c) => c.solutions.categories} tpl={{ index: "05", title: "", desc: "", image: "", programs: [], linkLabel: "Explore Programs", linkHref: "#featured" }} render={(row, i) => (
            <div className="space-y-2">
              <ImgBtn value={row.image} onChange={(v) => upd((c) => (c.solutions.categories[i].image = v))} />
              <div className="grid grid-cols-3 gap-2">
                <input className={inp} placeholder="Index" value={row.index} onChange={(e) => upd((c) => (c.solutions.categories[i].index = e.target.value))} />
                <input className={`${inp} col-span-2`} placeholder="Title" value={row.title} onChange={(e) => upd((c) => (c.solutions.categories[i].title = e.target.value))} />
              </div>
              <textarea rows={2} className={inp} placeholder="Description" value={row.desc} onChange={(e) => upd((c) => (c.solutions.categories[i].desc = e.target.value))} />
              <textarea rows={3} className={inp} placeholder="Programs (one per line)" value={arrToLines(row.programs)} onChange={(e) => upd((c) => (c.solutions.categories[i].programs = linesToArr(e.target.value)))} />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-500">Button label</label>
                  <input className={inp} placeholder="Explore Programs" value={row.linkLabel} onChange={(e) => upd((c) => (c.solutions.categories[i].linkLabel = e.target.value))} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-500">Button link — paste inner page URL</label>
                  <input data-testid={`solution-link-href-${i}`} className={inp} placeholder="/programs/your-page-slug" value={row.linkHref} onChange={(e) => upd((c) => (c.solutions.categories[i].linkHref = e.target.value))} />
                </div>
              </div>
            </div>
          )} />
        </F>
      </Section>

      <Section title="Flagship Programs (Featured)" open={open === "feat"} onToggle={() => setOpen(open === "feat" ? "" : "feat")}>
        <div className="grid grid-cols-2 gap-3">
          <F label="Heading lead"><input className={inp} value={feat.headingLead} onChange={(e) => upd((c) => (c.featured.headingLead = e.target.value))} /></F>
          <F label="Heading accent"><input className={inp} value={feat.headingAccent} onChange={(e) => upd((c) => (c.featured.headingAccent = e.target.value))} /></F>
        </div>
        <F label="Programs">
          <Rows arr={feat.items || []} path={(c) => c.featured.items} tpl={{ name: "", tag: "", desc: "", duration: "", image: "", learnLabel: "Learn More", learnHref: "#contact" }} render={(row, i) => (
            <div className="space-y-2">
              <ImgBtn value={row.image} onChange={(v) => upd((c) => (c.featured.items[i].image = v))} />
              <div className="grid grid-cols-2 gap-2">
                <input className={inp} placeholder="Name" value={row.name} onChange={(e) => upd((c) => (c.featured.items[i].name = e.target.value))} />
                <input className={inp} placeholder="Tag" value={row.tag} onChange={(e) => upd((c) => (c.featured.items[i].tag = e.target.value))} />
              </div>
              <textarea rows={2} className={inp} placeholder="Description" value={row.desc} onChange={(e) => upd((c) => (c.featured.items[i].desc = e.target.value))} />
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-500">Duration</label>
                  <input className={inp} placeholder="Duration" value={row.duration} onChange={(e) => upd((c) => (c.featured.items[i].duration = e.target.value))} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-500">Learn More label</label>
                  <input className={inp} placeholder="Learn More" value={row.learnLabel} onChange={(e) => upd((c) => (c.featured.items[i].learnLabel = e.target.value))} />
                </div>
                <div>
                  <label className="mb-1 block text-[11px] font-medium text-slate-500">Learn More link — paste inner page URL</label>
                  <input data-testid={`featured-learn-href-${i}`} className={inp} placeholder="/programs/your-page-slug" value={row.learnHref} onChange={(e) => upd((c) => (c.featured.items[i].learnHref = e.target.value))} />
                </div>
              </div>
            </div>
          )} />
        </F>
      </Section>

      <Section title="Footer" open={open === "footer"} onToggle={() => setOpen(open === "footer" ? "" : "footer")}>
        <F label="Eyebrow"><input className={inp} value={ft.eyebrow} onChange={(e) => upd((c) => (c.footer.eyebrow = e.target.value))} /></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Heading lead"><input className={inp} value={ft.headingLead} onChange={(e) => upd((c) => (c.footer.headingLead = e.target.value))} /></F>
          <F label="Heading accent"><input className={inp} value={ft.headingAccent} onChange={(e) => upd((c) => (c.footer.headingAccent = e.target.value))} /></F>
          <F label="Primary CTA label"><input className={inp} value={ft.ctaPrimary?.label} onChange={(e) => upd((c) => (c.footer.ctaPrimary.label = e.target.value))} /></F>
          <F label="Primary CTA link"><input className={inp} value={ft.ctaPrimary?.href} onChange={(e) => upd((c) => (c.footer.ctaPrimary.href = e.target.value))} /></F>
          <F label="Secondary CTA label"><input className={inp} value={ft.ctaSecondary?.label} onChange={(e) => upd((c) => (c.footer.ctaSecondary.label = e.target.value))} /></F>
          <F label="Secondary CTA link"><input className={inp} value={ft.ctaSecondary?.href} onChange={(e) => upd((c) => (c.footer.ctaSecondary.href = e.target.value))} /></F>
        </div>
        <F label="Brand name"><input className={inp} value={ft.brandName} onChange={(e) => upd((c) => (c.footer.brandName = e.target.value))} /></F>
        <F label="About text"><textarea rows={3} className={inp} value={ft.about} onChange={(e) => upd((c) => (c.footer.about = e.target.value))} /></F>
        <div className="grid grid-cols-3 gap-3">
          <F label="Email"><input className={inp} value={ft.email} onChange={(e) => upd((c) => (c.footer.email = e.target.value))} /></F>
          <F label="Phone"><input className={inp} value={ft.phone} onChange={(e) => upd((c) => (c.footer.phone = e.target.value))} /></F>
          <F label="Giant wordmark (big faded text at the very bottom)"><input data-testid="footer-wordmark-input" className={inp} value={ft.wordmark || ""} placeholder="Building H2O People" onChange={(e) => upd((c) => (c.footer.wordmark = e.target.value))} /></F>
          <F label="WhatsApp number (country code + number, digits only — used by the floating chat button)"><input data-testid="footer-whatsapp-input" className={inp} value={ft.whatsapp || ""} onChange={(e) => upd((c) => (c.footer.whatsapp = e.target.value))} /></F>
          <F label="Location"><input className={inp} value={ft.location} onChange={(e) => upd((c) => (c.footer.location = e.target.value))} /></F>
        </div>
        <F label="Explore links (one per line: Label | #anchor)">
          <textarea rows={4} className={inp} value={(ft.links || []).map((l) => `${l.label} | ${l.href}`).join("\n")}
            onChange={(e) => upd((c) => (c.footer.links = linesToArr(e.target.value).map((l) => { const [label, ...r] = l.split("|"); return { label: (label || "").trim(), href: r.join("|").trim() }; })))} />
        </F>
        <F label="Bottom tagline"><input className={inp} value={ft.tagline} onChange={(e) => upd((c) => (c.footer.tagline = e.target.value))} /></F>
      </Section>

      {pickCb && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-6" onClick={() => setPickCb(null)}>
          <div className="w-full max-w-4xl rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between"><h3 className="font-heading text-lg font-semibold">Choose an image</h3><button onClick={() => setPickCb(null)} className="text-slate-400 hover:text-slate-700">✕</button></div>
            <MediaLibrary onPick={(url) => { pickCb(url); setPickCb(null); }} />
          </div>
        </div>
      )}
    </div>
  );
}
